import React, { useState } from "react";
import { Alert, Box, Button, Checkbox, Typography } from "@mui/material";
import { isWorkspaceRowEligible, workspaceCompanyId } from "../../helper/companyEligibility";

const HIDDEN_COLUMNS = new Set(["stagedId", "staged_id", "id"]);

const displayValue = (value, column) => {
  if (column === "enrichment_origin") {
    return value === "source_saved_search"
      ? "Copied from another saved search"
      : "Enriched here";
  }
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ") || "—";
  return String(value);
};

export default function SavedSearchHistoricalResults({
  rows = [], columns = [], totalCount = 0, loading = false, error = "", onLoadMore,
  onExportSelected,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  if (totalCount <= 0 && rows.length === 0) return null;
  const visibleColumns = columns.filter((column) => !HIDDEN_COLUMNS.has(column));
  const canLoadMore = rows.length < totalCount && typeof onLoadMore === "function";
  const toggleRow = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return <Box data-testid="saved-search-historical-results" sx={{ mt: 4 }}>
    <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
      Previously enriched ({totalCount})
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      These saved answers are outside the current search results. Export all includes them without another charge.
    </Typography>
    <Box data-testid="saved-search-historical-scroll" sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <Box component="table" sx={{ borderCollapse: "collapse", width: "max-content", minWidth: "100%",
        "& th, & td": { borderBottom: "1px solid", borderColor: "divider", px: 1.5, py: 1.25,
          textAlign: "left", verticalAlign: "top", overflowWrap: "anywhere", minWidth: 160, maxWidth: 320 },
        "& th:first-of-type, & td:first-of-type": { minWidth: 96 } }}>
        <thead><tr><th scope="col">Select for export</th>{visibleColumns.map((column) =>
          <th key={column} scope="col">{column === "enrichment_origin" ? "Origin" : column}</th>
        )}</tr></thead>
        <tbody>{rows.map((row, index) => {
          const id = workspaceCompanyId(row);
          return <tr key={id || index}><td><Checkbox
            checked={Boolean(id && selectedIds.has(id))}
            disabled={!isWorkspaceRowEligible(row)}
            onChange={() => toggleRow(id)}
            inputProps={{ "aria-label": `Select retained ${row.Name || "company"}` }}
          /></td>{visibleColumns.map((column) =>
            <td key={column}>{displayValue(row[column], column)}</td>
          )}</tr>;
        })}</tbody>
      </Box>
    </Box>
    {typeof onExportSelected === "function" ? <Button
      onClick={() => onExportSelected(Array.from(selectedIds))}
      disabled={selectedIds.size === 0}
      sx={{ mt: 1 }}
    >Export selected historical</Button> : null}
    {rows.length < totalCount ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Showing {rows.length} of {totalCount} previously enriched companies.
    </Typography> : null}
    {error ? <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert> : null}
    {canLoadMore ? <Button onClick={onLoadMore} disabled={loading} sx={{ mt: 1 }}>
      {error ? "Retry historical results" : loading ? "Loading historical results…" : "Load more historical results"}
    </Button> : null}
  </Box>;
}
